import React, { useCallback, useEffect, useState } from 'react';
import { shouldFetchData, status } from '../../../../data/fetchStatus';
import Services from '../../../Services/services';
import { formatDate } from '../../../../utils/date';
import { ToastThemes } from '../../../Toasts/ToastOptions/toastOptions';
import useToast from '../../../Toasts/useToast';
import { addTransition, resizeElement } from '../../../../utils/accordeon';
import {
  waitingStatusDetail,
  waitingStatusClasses,
} from '../../../../data/statusDefinitions';

function ClientHistoryElement(props) {
  // #region Variables

  const [expertCommission, setExpertCommission] = useState(undefined);
  const [expertCommissionFetched, setExpertCommissionFetched] = useState(
    status.DEFAULT
  );

  const [clientInvoices, setClientInvoices] = useState(undefined);
  const [clientInvoicesFetched, setClientInvoicesFetched] = useState(
    status.DEFAULT
  );

  const [expertInvoices, setExpertInvoices] = useState(undefined);
  const [expertInvoicesFetched, setExpertInvoicesFetched] = useState(
    status.DEFAULT
  );

  const [clientUser, setClientUser] = useState(undefined);
  const [clientUserFetched, setClientUserFetched] = useState(status.DEFAULT);

  const [relationContract, setRelationContract] = useState(undefined);
  const [relationContractFetched, setRelationContractFetched] = useState(
    status.DEFAULT
  );

  const enqueueToast = useToast();

  // #endregion

  // #region Fetch

  const fetchExpertCommission = useCallback(async () => {
    if (
      props?.clientExpertRelation?.expertCommission?.id === undefined ||
      expertCommissionFetched === status.FETCHED
    )
      return;

    setExpertCommissionFetched(status.DEFAULT);

    try {
      const expertCommissionData =
        await Services.profileService.expertInvoiceByCommissionId(
          props?.clientExpertRelation?.expertCommission?.id
        );

      setExpertCommission(expertCommissionData);
    } catch (error) {
      console.error(error);
    }
  }, [
    props?.clientExpertRelation?.expertCommission?.id,
    expertCommissionFetched,
  ]);

  function processStatus() {
    let status = props?.clientExpertRelation?.status;

    if (
      props?.clientExpertRelation?.status === 3 &&
      props?.clientExpertRelation?.expertSigned
    )
      status++;

    return status;
  }

  const fetchClientInvoices = useCallback(async () => {
    if (clientInvoicesFetched === status.FETCHED) return;

    setClientInvoicesFetched(status.FETCHED);

    try {
      const documentsIdList = [];
      const invoices = [];

      for (const expertInvoice of expertCommission?.expertInvoices) {
        for (const [, id] of Object.entries(expertInvoice.expertInvoices))
          documentsIdList.push(id);
      }

      if (!documentsIdList.length) return;

      const documentsUploaded = await Services.documentService.documentListById(
        documentsIdList
      );

      const keyList = [];

      documentsUploaded?.forEach((document) => {
        invoices.push({
          name: document.name,
          downloadLink: '',
        });
        keyList.push(document.fileKey);
      });

      if (keyList.length === 0) return;

      const fileUrlList = await Services.documentService.fileListById(keyList);

      fileUrlList.forEach((url, idx) => (invoices[idx].downloadLink = url));

      setClientInvoices(invoices);

      // Refresh links after 1 hour (time in milliseconds)
      setTimeout(() => setClientInvoicesFetched(status.REFRESH), 3600000);
    } catch (error) {
      console.error(error);
    }
  }, [expertCommission, clientInvoicesFetched]);

  const fetchExpertInvoices = useCallback(async () => {
    if (
      expertInvoicesFetched === status.FETCHED ||
      expertCommission?.expertInvoices?.length === 0
    )
      return;

    setExpertInvoicesFetched(status.FETCHED);

    try {
      const invoicesStripeId = expertCommission?.expertInvoices?.map(
        (invoice) => invoice.stripeInvoiceId
      );

      const expertInvoicesUrl = await Services.profileService.expertInvoicesUrl(
        invoicesStripeId
      );

      setExpertInvoices(expertInvoicesUrl);
    } catch (error) {
      console.error(error);
    }
  }, [expertCommission, expertInvoicesFetched]);

  const fetchClientUser = useCallback(async () => {
    if (
      props?.clientExpertRelation?.clientId === undefined ||
      clientUserFetched === status.FETCHED
    )
      return;

    setClientUserFetched(status.FETCHED);

    try {
      const clientUserData = await Services.userService.userByID(
        props?.clientExpertRelation?.client?.userId
      );

      setClientUser(clientUserData);
    } catch (error) {
      console.error(error);
    }
  }, [props?.clientExpertRelation, clientUserFetched]);

  const fetchRelationContract = useCallback(async () => {
    if (relationContractFetched === status.FETCHED) return;

    setRelationContractFetched(status.FETCHED);

    try {
      const contractDocument =
        await Services.documentService.documentsByClientExpert(
          props?.clientExpertRelation?.clientId,
          props?.clientExpertRelation?.expertId,
          'expert'
        );

      if (!contractDocument.length) return;

      const urlList = await Services.documentService.fileListById([
        contractDocument[0].fileKey,
      ]);

      if (!urlList.length) return;

      setRelationContract(urlList[0]);

      setTimeout(() => setRelationContractFetched(status.REFRESH), 3600000);
    } catch (error) {
      console.error(error);
    }
  }, [props?.clientExpertRelation, relationContractFetched]);

  // #endregion

  // #region UseEffects

  useEffect(() => {
    if (shouldFetchData(expertCommissionFetched)) fetchExpertCommission();
  }, [expertCommissionFetched, fetchExpertCommission]);

  useEffect(() => {
    if (
      expertCommission !== undefined &&
      shouldFetchData(clientInvoicesFetched)
    )
      fetchClientInvoices();
  }, [expertCommission, clientInvoicesFetched, fetchClientInvoices]);

  useEffect(() => {
    if (
      expertCommission !== undefined &&
      shouldFetchData(expertInvoicesFetched)
    )
      fetchExpertInvoices();
  }, [expertCommission, expertInvoicesFetched, fetchExpertInvoices]);

  useEffect(() => {
    if (
      props?.clientExpertRelation?.status >= 2 &&
      shouldFetchData(clientUserFetched)
    )
      fetchClientUser();
  }, [props?.clientExpertRelation, clientUserFetched, fetchClientUser]);

  useEffect(() => {
    if (shouldFetchData(relationContractFetched)) fetchRelationContract();
  }, [relationContractFetched, fetchRelationContract]);

  // #endregion

  // #region Functions

  function showDetails() {
    const elm = document.getElementById(props?.id);

    if (elm.classList.contains('active')) {
      elm.getElementsByClassName('element-details-open')[0].style.maxHeight =
        '0px';
      elm.classList.remove('active');
    } else {
      elm.classList.add('active');
      const tabElm = elm.getElementsByClassName('element-details-open')[0];
      addTransition(elm, tabElm, 50, 400);
    }
  }

  function switchAccordion(e) {
    const accordionBtnElm = e.target;
    const { accordionbtn } = accordionBtnElm.dataset;

    const accordionElm = document.querySelector(
      `[data-accordion=${accordionbtn}]`
    );

    if (!accordionElm) return;

    if (accordionBtnElm.classList.contains('active')) {
      accordionElm.style.maxHeight = '0px';
      accordionBtnElm.classList.remove('active');
    } else {
      accordionBtnElm.classList.add('active');
      resizeElement(accordionBtnElm, accordionElm);

      const containerElm = document.getElementById(props?.id);
      const targetElm = containerElm.getElementsByClassName(
        'element-details-open'
      )[0];
      addTransition(containerElm, targetElm, 100, 500);
    }
  }

  function CalculateCumulClient() {
    let clientAmount = 0;

    expertCommission?.expertInvoices?.map(
      (invoice) => (clientAmount += invoice.clientAmount)
    );

    return parseFloat(clientAmount / 100).toFixed(2);
  }

  async function CopyToClipboard(value) {
    navigator.clipboard.writeText(value);

    enqueueToast(value, {
      theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
      header: 'Press-papier',
      duration: 3000,
    });
  }

  // #endregion

  return (
    <>
      <div className="element-container" id={props?.id}>
        <div className="elements-data">
          <div className="history-element-demand">
            <span className="data-title">Nom client</span>
            <span className="data">{`${props?.clientExpertRelation?.client?.firstName} ${props?.clientExpertRelation?.client?.lastName}`}</span>
          </div>
          <div className="history-element-demand">
            <span className="data-title">Numéro client</span>
            <span className="data">{`#${props?.clientExpertRelation?.referenceNumber}`}</span>
          </div>
          <div
            className="history-element-demand"
            style={{ 'align-items': 'center' }}
          >
            <span className="data-title">Statut</span>
            {
              <span
                className={`status-demand ${
                  waitingStatusClasses[processStatus()]
                }`}
                style={{ margin: 'auto' }}
              >
                {waitingStatusDetail.expert[processStatus()]}
              </span>
            }
          </div>
          <div className="icons-container">
            {props?.clientExpertRelation?.status >= 2 && (
              <>
                <div
                  className="icones-contact contact-phone"
                  onClick={() => CopyToClipboard(clientUser?.phone)}
                >
                  <span>{clientUser?.phone}</span>
                </div>
                <div
                  className="icones-contact contact-email"
                  onClick={() => CopyToClipboard(clientUser?.email)}
                >
                  <span>{clientUser?.email}</span>
                </div>
              </>
            )}

            <div className="detail-icon" onClick={(e) => showDetails(e)}></div>
          </div>
        </div>
        <div className="element-details-open client-contract-details-main">
          <div className="client-contract-details-elmnt">
            Cumul tarif apporté du client :{' '}
            {
              <span className="history-title-span">
                {CalculateCumulClient()}€
              </span>
            }
          </div>
          <div className="client-contract-details-elmnt">
            Contrat/Lettre de mission :{' '}
            {
              <span className="history-title-span">
                {relationContract !== undefined && (
                  <a
                    href={relationContract}
                    download={`Lettre_mission_${props?.clientExpertRelation?.client?.firstName}_${props?.clientExpertRelation?.client?.lastName}`}
                    className="history-contract-item"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Lettre_mission_
                    {props?.clientExpertRelation?.client?.firstName}_
                    {props?.clientExpertRelation?.client?.lastName}
                  </a>
                )}
              </span>
            }
          </div>
          {clientInvoices !== undefined && (
            <div className="client-contract-history">
              <button
                className="contract-history-header"
                data-accordionbtn={`history-contract-${props?.id}`}
                onClick={(e) => switchAccordion(e)}
              >
                <span className="history-header-title">
                  Facture émise par votre cabinet
                </span>
                <span className="history-header-arrow"></span>
              </button>
              <div
                className="contract-history-accordion"
                data-accordion={`history-contract-${props?.id}`}
              >
                {clientInvoices !== undefined &&
                  clientInvoices.map((invoice) => (
                    <a
                      key={invoice.name}
                      href={invoice.downloadLink}
                      download={invoice.name}
                      className="history-contract-item"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {invoice.name}
                    </a>
                  ))}
              </div>
            </div>
          )}
          {expertInvoices !== undefined && (
            <div className="client-contract-history">
              <button
                className="contract-history-header"
                data-accordionbtn={`history-invoices-${props?.id}`}
                onClick={(e) => switchAccordion(e)}
              >
                <span className="history-header-title">
                  Facture de commission CEC
                </span>
                <span className="history-header-arrow"></span>
              </button>
              <div
                className="contract-history-accordion"
                data-accordion={`history-invoices-${props?.id}`}
              >
                {expertInvoices !== undefined &&
                  expertInvoices.map((invoice, idx) => (
                    <a
                      key={'invoice' + idx}
                      href={invoice}
                      download={`Facture-${formatDate(
                        expertCommission?.expertInvoices[idx]?.paydAt,
                        'DD_MM_YYYY'
                      )}.pdf`}
                      className="history-contract-item"
                    >
                      {`Facture-${formatDate(
                        expertCommission?.expertInvoices[idx]?.paydAt,
                        'DD_MM_YYYY'
                      )}.pdf`}
                    </a>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ClientHistoryElement;
